/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  selectEnableHotelGroups,
  selectReportType,
  selectIsDemoUser,
  selectTabValue,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import {
  selectTrendTimeDateSelected,
  selectIsCumulative,
  selectTrendTimeCustomDate,
  selectTrendTimeEndDate,
  selectTrendTimeStartDate,
  selectTrendTimeWeeklyView,
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeAlignment,
  selectTrendTimeDateComparison,
  selectIsSegmentDetailed,
  selectTrendSegmentChartBreakdown,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendTimeFilterList,
  selectActiveTrendTab,
  selectIsPercentageChange,
  selectTabularView,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectGranularity,
  selectIsTrendDeAggregate,
  selectTrendTimeTabularFilterList,
  selectIsMovingAverage,
  selectMovingAverage,
  selectTrendTimeSegmentFilterList,
  selectTrendTimeSegmentTabularFilterList,
  selectIsFocusOnSet,
  selectIsFocusAllSelected,
  selectTrendHotelSegmentTabularFilterList,
  selectTrendHotelSegmentFilterList,
  selectTrendHotelFocusOn,
  selectTrendHotelNormalizedView,
  selectEnableSecondaryHotelGroups,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectTrendTimeAlignmentToggle,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  differenceInDays,
  endOfMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSaturday,
  isSunday,
  lastDayOfYear,
  nextSaturday,
  previousSunday,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { KEYS, REPORT_TYPE } from 'modules/dashboard/constants';
import {
  getBookingDateSchema,
  getComparisonBookingDateSchema,
  getComparisonStayDateSchema,
  getDashboardPmsSyncSchema,
  getGranularitySchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupOrderSchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getIsForecastSchema,
  getMaxStayDateSchema,
  getMovingAverageSchema,
  getNormalizedViewSchema,
  getPmsSyncToggleSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getSegmentFocusOnSchema,
  getSymmetricComparisonSchema,
  getTargetDateSchema,
  getTimeSegmentBreakdownAndFocusOnSchema,
  getTrendTimeAlignmentAndDateComparisonSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import writeLog from 'modules/common/utils/filter-log';
import { compareWindows } from 'modules/common/ui-controller';
import { getGranularity } from '../functions';
import { GRANULARITY } from '../constants';
import calculateComparisonDates, {
  getWeeklyAlignedCustomDate,
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';

/**
 * Custom hook to generate Trend Time widget filter schemas
 */
const useTrendTime = () => {
  const dispatch = useDispatch();
  //
  const alignment = useSelector(selectTrendTimeAlignment);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const bookingDate = useSelector(selectTrendBookingDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const targetStartDate = useSelector(selectWeeklyTrendStartDate);
  const targetEndDate = useSelector(selectWeeklyTrendEndDate);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const isCumulative = useSelector(selectIsCumulative);
  const startDateComparison = useSelector(selectTrendTimeStartDate);
  const endDateComparison = useSelector(selectTrendTimeEndDate);
  const customDate = useSelector(selectTrendTimeCustomDate);
  const selectedDateComparison = useSelector(selectTrendTimeDateSelected);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const isTrendForecast = useSelector(selectIsForecast);
  const filterList = useSelector(selectTrendTimeFilterList);
  const tabularFilterList = useSelector(selectTrendTimeTabularFilterList);
  const activeTab = useSelector(selectActiveTrendTab);
  const reportType = useSelector(selectReportType);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isTabular = useSelector(selectTabularView);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const tabValue = useSelector(selectTabValue);
  const TimeSegmentFilterList = useSelector(selectTrendTimeSegmentFilterList);
  const SegmentTabularFilterList = useSelector(selectTrendTimeSegmentTabularFilterList);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isFocusAllSelected = useSelector(selectIsFocusAllSelected);
  const hotelSegmentFilterList = useSelector(selectTrendHotelSegmentFilterList);
  const hotelSegmentTabularFilterList = useSelector(selectTrendHotelSegmentTabularFilterList);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const startDate = useSelector(selectTrendStartDate);
  const endDate = useSelector(selectTrendEndDate);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  const TAB_VALUE = useSelector(selectTabList);
  //
  useEffect(() => {
    if (isTabular) {
      if (weeklyViewToggle) {
        dispatch(trendActions.setTrendStartDate(targetStartDate));
        dispatch(trendActions.setTrendEndDate(targetEndDate));
      } else if (granularity === GRANULARITY.DAILY) {
        dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
        dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
      } else {
        dispatch(trendActions.setTrendStartDate(adjustedStartDate));
        dispatch(trendActions.setTrendEndDate(adjustedEndDate));
      }
    } else if (isCumulative) {
      dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
      dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
    } else {
      dispatch(
        trendActions.setTrendStartDate(weeklyViewToggle ? targetStartDate : targetDate?.startDate)
      );
      dispatch(
        trendActions.setTrendEndDate(weeklyViewToggle ? targetEndDate : targetDate?.endDate)
      );
    }
  }, [
    isTabular,
    granularity,
    isCumulative,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    targetDate,
    adjustedEndDate,
    adjustedStartDate,
  ]);

  //
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      !hotelId ||
      (!dateComparison && !alignment && !bookingDate) ||
      (dateComparison && dateComparison[0] === KEYS.CUSTOM && endDateComparison === null)
    ) {
      return;
    }
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getTrendTimeAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const isForecastFilter = getIsForecastSchema(visualFilters, isTrendForecast);
    const targetDateFilter = getTargetDateSchema(visualFilters, startDate, endDate);
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const deAggregateSegmentAndFocusOnFilter = getTimeSegmentBreakdownAndFocusOnSchema(
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
      hotelFocusOn?.id === hotelId?.id
    );
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const granularityFilter = getGranularitySchema(visualFilters, granularity);
    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    const movingAverageFilter = getMovingAverageSchema(visualFilters, movingAverage);
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    //
    let hotelIdFilter = [];
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    if (enableHotelGroups) {
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    //
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    // For weekly views, the selected stay-date range is adjusted to a Sunday–Saturday
    // range. Apply the same offset between the original selected end date and the custom
    // comparison end date to the adjusted range to derive the new custom comparison period.
    let customComparisonDate = {
      startDate: startDateComparison,
      endDate: endDateComparison,
    };
    const isWeeklyAdjusted = weeklyViewToggle && !isCumulative;
    
    if (
      dateComparison === compareWindows.cus &&
      isWeeklyAdjusted &&
      endDateComparison &&
      (activeTab === REPORT_TYPE.TREND_TIME || (activeTab === REPORT_TYPE.TREND_SEGMENT && isTrendDeAggregate))
    ) {
      customComparisonDate = getWeeklyAlignedCustomDate(
        { startDate, endDate },
        targetDate?.endDate,
        customComparisonDate
      );
    }
    //
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      { startDate, endDate },
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      customComparisonDate,
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    //
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, endDate);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(trendActions.setIsTrendLeapComparison(hasLeap));
    // If the comparison is unaligned and the offset is monthly, check if the number of days in the compared range is greater than the selected range. If so, set a flag to indicate that this is a special case for unaligned month comparisons.
    const isUnaligned = alignment?.label === 'Unaligned';
    const isPreviousMonthComparison = dateComparison === compareWindows.pm;

    const selectedDays = differenceInDays(new Date(endDate), new Date(startDate));
    const comparedDays = differenceInDays(adjustedStayDate?.endDate, adjustedStayDate?.startDate);
    dispatch(
      trendActions.setIsTrendTimeUnalignedMonthComparisonSpecialDate(
        isUnaligned && isPreviousMonthComparison && comparedDays > selectedDays
      )
    );
    //
    if (
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      symmetricComparisonFilter &&
      hotelGroupOrderFilter &&
      segmentFocusOnFilter
    ) {
      let filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        symmetricComparisonFilter,
        roomForecastFilter,
        revenueForecastFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (!isPercentageChange) {
        filters = [...filters, ...deAggregateSegmentAndFocusOnFilter];
      }
      if (isMovingAverage) {
        filters = [...filters, movingAverageFilter];
      }
      // tabular filter list
      let tabularFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        symmetricComparisonFilter,
        roomForecastFilter,
        revenueForecastFilter,
        hotelGroupOrderFilter,
        movingAverageFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (!isPercentageChange) {
        tabularFilters = [...tabularFilters, ...deAggregateSegmentAndFocusOnFilter];
      }
      if (!weeklyViewToggle) {
        tabularFilters = [...tabularFilters, granularityFilter];
      }
      // dispatch filter list for trend time based on active tab
      if (
        (tabValue === TAB_VALUE.TREND_TIME || tabValue === TAB_VALUE.PBI_TREND) &&
        reportType === REPORT_TYPE.TREND_TIME &&
        activeTab === REPORT_TYPE.TREND_TIME
      ) {
        if (JSON.stringify(filters) !== JSON.stringify(filterList)) {
          writeLog('V2 Trend Time Filters', filters);
          dispatch(trendActions.updateTrendTimeFilterList(filters));
        }
        if (
          JSON.stringify(tabularFilters) !== JSON.stringify(tabularFilterList) &&
          activeTab === REPORT_TYPE.TREND_TIME
        ) {
          writeLog('V2 Trend Time Tabular Filters', tabularFilters);
          dispatch(trendActions.updateTrendTimeTabularFilterList(tabularFilters));
        }
      }
      // dispatch filter list for time segment visuals based on active tab
      if (activeTab === REPORT_TYPE.TREND_SEGMENT) {
        if (JSON.stringify(filters) !== JSON.stringify(TimeSegmentFilterList)) {
          writeLog('V2 Trend Time Segment Filters', filters);
          dispatch(trendActions.updateTrendTimeSegmentFilterList(filters));
        }
        if (
          JSON.stringify(tabularFilters) !== JSON.stringify(SegmentTabularFilterList) &&
          activeTab === REPORT_TYPE.TREND_SEGMENT
        ) {
          writeLog('V2 Trend Time Segment Tabular Filters', tabularFilters);
          dispatch(trendActions.updateTrendTimeSegmentTabularFilterList(tabularFilters));
        }
        let hotelSegmentFilters = [
          ...pmsSyncFilter,
          bookingDateFilter,
          targetDateFilter,
          ...hotelFilters,
          ...segmentFocusOnFilter,
          ...pmsSyncSchema,
          normalizedFilter,
        ];
        if (!isPercentageChange) {
          hotelSegmentFilters = [...hotelSegmentFilters, ...deAggregateSegmentAndFocusOnFilter];
        }
        if (isMovingAverage) {
          hotelSegmentFilters = [...hotelSegmentFilters, movingAverageFilter];
        }
        // tabular filter list
        let hotelSegmentTabularFilters = [
          ...pmsSyncFilter,
          bookingDateFilter,
          targetDateFilter,
          ...hotelFilters,
          hotelGroupOrderFilter,
          movingAverageFilter,
          ...segmentFocusOnFilter,
          ...pmsSyncSchema,
          normalizedFilter,
        ];
        if (!isPercentageChange) {
          hotelSegmentTabularFilters = [
            ...hotelSegmentTabularFilters,
            ...deAggregateSegmentAndFocusOnFilter,
          ];
        }
        if (!weeklyViewToggle) {
          hotelSegmentTabularFilters = [...hotelSegmentTabularFilters, granularityFilter];
        }
        if (
          activeTab === REPORT_TYPE.TREND_SEGMENT &&
          isTrendDeAggregate &&
          hotelFocusOn?.id !== hotelId?.id
        ) {
          dispatch(trendActions.updateTrendHotelSegmentFilterList([]));
          dispatch(trendActions.updateTrendHotelSegmentTabularFilterList([]));
          dispatch(trendActions.updateTrendHotelSegmentFilterList(hotelSegmentFilters));
          dispatch(
            trendActions.updateTrendHotelSegmentTabularFilterList(hotelSegmentTabularFilters)
          );
        }
        if (JSON.stringify(hotelSegmentFilters) !== JSON.stringify(hotelSegmentFilterList)) {
          writeLog('Trend Hotel Segment Filters', hotelSegmentFilters);
        }
        if (
          JSON.stringify(hotelSegmentTabularFilters) !==
            JSON.stringify(hotelSegmentTabularFilterList) &&
          activeTab === REPORT_TYPE.TREND_SEGMENT
        ) {
          writeLog('Trend Hotel Segment Tabular Filters', hotelSegmentTabularFilters);
        }
      }
    }
  }, [
    hotelFocusOn,
    bookingDate,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    pmsSyncToggle,
    weeklyViewToggle,
    symmetricComparisonToggle,
    targetStartDate,
    targetEndDate,
    isCumulative,
    startDateComparison,
    endDateComparison,
    selectedDateComparison,
    customDate,
    isDetailed,
    focusOn,
    enableHotelGroups,
    isTrendForecast,
    reportType,
    activeTab,
    isPercentageChange,
    granularity,
    adjustedStartDate,
    adjustedEndDate,
    isTabular,
    isTrendDeAggregate,
    isMovingAverage,
    movingAverage,
    tabValue,
    IsFocusOnSet,
    isFocusAllSelected,
    normalizedViewToggle,
    enableSecondaryHotelGroups,
    startDate,
    endDate,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    alignmentToggle,
    resetBookingDate,
  ]);
  // Triggered when targetDate value changes
  useEffect(() => {
    // Set stay dates based on granularity
    const trendGranularity = getGranularity(targetDate);
    dispatch(trendActions.setGranularity(trendGranularity));
    if (trendGranularity === GRANULARITY.WEEKLY) {
      if (isSunday(new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            previousSunday(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSaturday(new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(nextSaturday(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.MONTHLY) {
      if (isFirstDayOfMonth(new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            startOfMonth(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isLastDayOfMonth(new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(endOfMonth(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.YEARLY) {
      if (isSameDay(startOfYear(new Date(targetDate.startDate)), new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            startOfYear(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSameDay(lastDayOfYear(new Date(targetDate.endDate)), new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(lastDayOfYear(new Date(targetDate.endDate)).toISOString())
        );
      }
    }
  }, [targetDate]);
};
//
export default useTrendTime;
